import {useEffect, useRef, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {FileOutput, History, MoveRight} from "lucide-react";
import {Link} from "react-router-dom";
import {
    COUNTRY_SCOPE_STORAGE_KEY,
    getAdminCountryScope,
    resolveAdminCountryScope,
} from "../../shared/api/countryScope";
import {useCapabilities} from "../../shared/auth/CapabilitiesContext";
import {DataState} from "../../shared/components/DataState";
import {MutationResultBanner} from "../../shared/components/MutationResultBanner";
import {ServerPagination} from "../../shared/components/ServerPagination";
import {StatusBadge} from "../../shared/components/StatusBadge";
import {formatExactMoney} from "../../shared/finance/decimal";
import {mapFinanceError} from "../../shared/finance/errors";
import {formatDateTime, formatNumber} from "../../shared/utils/format";
import {useTranslation} from "../../shared/i18n";
import {exportSettlements, listSettlementExports, settlementKeys} from "./api";
import "./settlements.css";

const RENDER_TONE = {
    pending: "neutral",
    rendering: "warning",
    finalized: "success",
    failed: "danger",
};

const renderTone = (status) => RENDER_TONE[status] || "neutral";
const renderLabel = (status, t) => t(`money.settlements.exports.render.${RENDER_TONE[status] ? status : "unknown"}`);

const ExportResult = ({result}) => {
    const {t} = useTranslation();
    if (!result) return null;
    return (
        <section className="settlement-export-result" aria-labelledby="settlement-export-result-title">
            <header>
                <div>
                    <p className="admin-eyebrow">{t("money.settlements.exports.resultEyebrow")}</p>
                    <h2 id="settlement-export-result-title">{t("money.settlements.exports.resultTitle")}</h2>
                </div>
                <Link className="admin-button admin-button-secondary" to={`/settlements/exports/${result.export_id}`}>
                    {t("money.settlements.exports.openResult", {id: result.export_id})}
                </Link>
            </header>
            <div className="settlement-report-metrics">
                <span><small>{t("money.settlements.exports.totalStatements")}</small><strong>{formatNumber(result.summary.total_count)}</strong></span>
                <span><small>{t("money.settlements.exports.readyPayable", {count: formatNumber(result.summary.ready_count)})}</small><strong>{formatExactMoney(result.summary.ready_payable_amount)}</strong></span>
                <span><small>{t("money.settlements.exports.includedCount")}</small><strong>{formatNumber(result.included_statement_ids.length)}</strong></span>
                <span><small>{t("money.settlements.exports.totalAmount")}</small><strong>{formatExactMoney(result.summary.total_payable_amount)}</strong></span>
            </div>
            <div className="settlement-export-evidence">
                <div>
                    <h3>{t("money.settlements.exports.includedTitle")}</h3>
                    {result.included_statement_ids.length ? (
                        <div className="settlement-result-links">
                            {result.included_statement_ids.map((statementId) => (
                                <Link key={statementId} to={`/settlements/${statementId}`} aria-label={t("money.settlements.exports.statement", {id: statementId})}>
                                    {t("money.settlements.exports.statement", {id: statementId})}
                                </Link>
                            ))}
                        </div>
                    ) : <p className="admin-muted">{t("money.settlements.exports.includedEmpty")}</p>}
                </div>
                <div>
                    <h3>{t("money.settlements.exports.excludedTitle")}</h3>
                    {result.excluded_statement_ids.length ? (
                        <p className="settlement-export-id-list">{result.excluded_statement_ids.map((id) => `#${id}`).join(", ")}</p>
                    ) : <p className="admin-muted">{t("money.settlements.exports.excludedEmpty")}</p>}
                </div>
            </div>
        </section>
    );
};

const ExportHistory = ({page, data, pending, onPageChange}) => {
    const {t} = useTranslation();
    if (!data?.results.length) {
        return <DataState title={t("money.settlements.exports.emptyTitle")} copy={t("money.settlements.exports.emptyCopy")} />;
    }
    return (
        <>
            <div className="settlement-export-history" aria-label={t("money.settlements.exports.historyAria")}>
                {data.results.map((record) => {
                    return (
                        <article className="settlement-export-row" key={record.id}>
                            <div>
                                <p className="admin-strong">{t("money.settlements.exports.record", {id: record.id})}</p>
                                <p className="admin-muted">{formatDateTime(record.period_start)} - {formatDateTime(record.period_end)}</p>
                            </div>
                            <div className="settlement-export-row-status">
                                <StatusBadge tone={renderTone(record.render_status)}>{renderLabel(record.render_status, t)}</StatusBadge>
                                <span>{record.format.toUpperCase()}</span>
                            </div>
                            <div className="admin-muted">{t("money.settlements.exports.meta", {country: record.country_id || "-", operator: record.created_by_id || "-"})}</div>
                            <Link className="settlement-row-link" to={`/settlements/exports/${record.id}`} aria-label={t("money.settlements.exports.openAria", {id: record.id})} title={t("money.settlements.exports.open")}>
                                <MoveRight aria-hidden="true" size={17} />
                            </Link>
                        </article>
                    );
                })}
            </div>
            <ServerPagination
                count={data.count}
                page={page}
                next={data.next}
                previous={data.previous}
                pending={pending}
                onPageChange={onPageChange}
            />
        </>
    );
};

export const SettlementExportsPage = () => {
    const {t} = useTranslation();
    const capabilities = useCapabilities();
    const queryClient = useQueryClient();
    const [countryScope, setCountryScope] = useState(() => resolveAdminCountryScope({
        role: capabilities.user?.role,
        allowedCountryIds: capabilities.scope?.country_ids || [],
        storedScope: localStorage.getItem(COUNTRY_SCOPE_STORAGE_KEY) ?? getAdminCountryScope(),
    }));
    const activeCountryScopeRef = useRef(countryScope);
    const [periodStart, setPeriodStart] = useState("");
    const [periodEnd, setPeriodEnd] = useState("");
    const [page, setPage] = useState(1);
    const [formError, setFormError] = useState("");
    const [result, setResult] = useState(null);
    const [mutationResult, setMutationResult] = useState(null);

    useEffect(() => {
        const handleCountryChange = (event) => {
            const nextScope = event.detail?.countryId == null ? null : String(event.detail.countryId);
            activeCountryScopeRef.current = nextScope;
            setCountryScope(nextScope);
            setPeriodStart("");
            setPeriodEnd("");
            setPage(1);
            setFormError("");
            setResult(null);
            setMutationResult(null);
        };
        window.addEventListener("sorpa-admin-country-scope-change", handleCountryChange);
        return () => window.removeEventListener("sorpa-admin-country-scope-change", handleCountryChange);
    }, []);

    const exportsQuery = useQuery({
        queryKey: settlementKeys.exports(countryScope, page),
        queryFn: () => listSettlementExports({page}),
        enabled: countryScope !== null,
        retry: false,
        placeholderData: (previousData, previousQuery) =>
            previousQuery?.queryKey?.[1] === String(countryScope) ? previousData : undefined,
    });

    const exportMutation = useMutation({
        mutationFn: ({requestScope, requestPeriodStart, requestPeriodEnd}) => exportSettlements({
            countryScope: requestScope,
            periodStart: requestPeriodStart,
            periodEnd: requestPeriodEnd,
        }),
        onSuccess: (response, variables) => {
            void queryClient.invalidateQueries({queryKey: settlementKeys.exports(variables.requestScope, 1)});
            if (activeCountryScopeRef.current !== variables.requestScope) return;
            setResult(response);
            setMutationResult({kind: "success", message: t("money.settlements.exports.created", {id: response.export_id})});
        },
        onError: (error, variables) => {
            if (activeCountryScopeRef.current !== variables.requestScope) return;
            setMutationResult(mapFinanceError(error));
        },
    });

    const submit = (event) => {
        event.preventDefault();
        if (!periodStart || !periodEnd) {
            setFormError(t("money.settlements.exports.periodRequired"));
            return;
        }
        if (periodStart >= periodEnd) {
            setFormError(t("money.settlements.exports.periodOrder"));
            return;
        }
        setFormError("");
        setResult(null);
        setMutationResult(null);
        exportMutation.mutate({
            requestScope: countryScope,
            requestPeriodStart: periodStart,
            requestPeriodEnd: periodEnd,
        });
    };

    const globalScope = countryScope === "0";
    const canExport = capabilities.hasAction("settlements.export");

    return (
        <div className="admin-page settlement-exports-page">
            <header className="admin-page-header settlement-page-header">
                <div>
                    <h1 className="admin-page-title">{t("money.settlements.exports.title")}</h1>
                    <p className="admin-page-copy">{t("money.settlements.exports.copy")}</p>
                </div>
            </header>

            {canExport ? (
                <section className="settlement-export-command" aria-labelledby="settlement-export-command-title">
                    <div>
                        <p className="admin-eyebrow">{t("money.settlements.exports.filterEyebrow")}</p>
                        <h2 id="settlement-export-command-title">{t("money.settlements.exports.createTitle")}</h2>
                    </div>
                    <form className="admin-filter-form settlement-export-form" onSubmit={submit}>
                        <label className="admin-field"><span>{t("money.settlements.exports.periodFrom")}</span><input type="date" value={periodStart} onChange={(event) => setPeriodStart(event.target.value)} /></label>
                        <label className="admin-field"><span>{t("money.settlements.exports.periodTo")}</span><input type="date" value={periodEnd} onChange={(event) => setPeriodEnd(event.target.value)} /></label>
                        <button className="admin-button admin-button-primary" type="submit" disabled={globalScope || exportMutation.isPending}>
                            <FileOutput aria-hidden="true" size={16} /> {t("money.settlements.exports.submit")}
                        </button>
                        {formError ? <p className="settlement-filter-error" role="alert">{formError}</p> : null}
                    </form>
                    {globalScope ? <p className="settlement-scope-warning">{t("money.settlements.scopeWarning")}</p> : null}
                </section>
            ) : null}

            <MutationResultBanner result={mutationResult} />
            <ExportResult result={result} />

            <section className="settlement-section" aria-labelledby="settlement-export-history-title">
                <header className="settlement-section-heading">
                    <div>
                        <p className="admin-eyebrow">{t("money.settlements.exports.filesEyebrow")}</p>
                        <h2 id="settlement-export-history-title">{t("money.settlements.exports.historyTitle")}</h2>
                    </div>
                    <History aria-hidden="true" size={18} />
                </header>
                {exportsQuery.isLoading ? <div className="admin-table-skeleton" aria-label={t("money.settlements.exports.loading")}><span /><span /><span /></div> : null}
                {exportsQuery.isError ? <DataState title={t("money.settlements.exports.loadError")} copy={mapFinanceError(exportsQuery.error).message} /> : null}
                {!exportsQuery.isLoading && !exportsQuery.isError ? <ExportHistory page={page} data={exportsQuery.data} pending={exportsQuery.isFetching} onPageChange={setPage} /> : null}
            </section>
        </div>
    );
};
